import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { RiderService, Rider } from '../../../services/riders/rider.service';
import { SnackbarService } from '../../../services/design/snackbar.service';
import { RiderVerificationDetailComponent } from './rider-verification-detail.component';
import { AdminPaginatorComponent } from '../../../Design/admin-paginator.component';

@Component({
  selector: 'app-rider-verification-list',
  standalone: true,
  imports: [CommonModule, RouterModule, RiderVerificationDetailComponent, AdminPaginatorComponent],
  template: `
    <div class="p-8 space-y-8 animate-in fade-in duration-700">
      <!-- Header -->
      <div class="flex items-center justify-between bg-base-100/40 backdrop-blur-xl p-8 rounded-[2.5rem] border border-white/10 shadow-2xl">
        <div>
          <h1 class="text-3xl font-black bg-gradient-to-r from-warning to-error bg-clip-text text-transparent">Verification Requests</h1>
          <p class="text-sm font-medium opacity-60 mt-1">Review documentation for pending delivery partners</p>
        </div>
        <div class="flex items-center gap-4">
           <div class="px-6 py-3 rounded-2xl bg-warning/10 border border-warning/20 flex items-center gap-3">
              <span class="w-3 h-3 rounded-full bg-warning animate-pulse"></span>
              <span class="text-sm font-black text-warning uppercase">{{ pendingRiders().length }} Pending Approval</span>
           </div>
        </div>
      </div>

      <!-- Content Grid -->
      @if (isLoading()) {
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          @for (i of [1,2,3]; track i) {
            <div class="h-64 rounded-[2rem] bg-base-200/50 animate-pulse border border-white/5"></div>
          }
        </div>
      } @else if (pendingRiders().length > 0) {
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          @for (rider of paginatedRiders(); track rider.id) {
            <div 
              (click)="viewDetail(rider)"
              (keydown.enter)="viewDetail(rider)"
              tabindex="0"
              role="button"
              class="group relative bg-base-100/40 backdrop-blur-md rounded-[2.5rem] border border-white/5 p-8 shadow-xl hover:shadow-2xl hover:bg-white/5 transition-all cursor-pointer overflow-hidden"
            >
              <!-- Glass background decoration -->
              <div class="absolute -top-12 -right-12 w-32 h-32 bg-primary/20 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              
              <div class="relative z-10 space-y-6">
                <!-- User Profile -->
                <div class="flex items-center gap-5">
                  <div class="avatar">
                    <div class="w-16 h-16 rounded-3xl bg-gradient-to-br from-warning/20 to-error/20 flex items-center justify-center border border-white/10 shadow-lg overflow-hidden">
                      @if (rider.profile_photo_url) {
                        <img [src]="rider.profile_photo_url" alt="Avatar" class="w-full h-full object-cover">
                      } @else {
                        <span class="text-2xl font-black text-warning">{{ rider.full_name[0] }}</span>
                      }
                    </div>
                  </div>
                  <div>
                    <h3 class="text-lg font-black">{{ rider.full_name }}</h3>
                    <p class="text-xs font-mono opacity-40 uppercase tracking-widest">{{ rider.code || 'PID-'+ rider.id.slice(0,8) }}</p>
                  </div>
                </div>

                <div class="divider opacity-5"></div>

                <!-- Info Grid -->
                <div class="grid grid-cols-2 gap-4">
                  <div class="space-y-1">
                    <p class="text-[10px] font-black uppercase opacity-30 tracking-tighter">Onboarding Step</p>
                    <p class="text-xs font-bold text-primary">{{ rider.onboarding_step }}</p>
                  </div>
                  <div class="space-y-1">
                    <p class="text-[10px] font-black uppercase opacity-30 tracking-tighter">Vehicle Type</p>
                    <p class="text-xs font-bold capitalize">{{ rider.vehicle_type.replace('_', ' ') }}</p>
                  </div>
                </div>

                <!-- Action Button -->
                <button class="btn btn-block btn-ghost bg-warning/10 hover:bg-warning text-warning hover:text-warning-content border-none rounded-2xl h-12 transition-all font-black uppercase text-xs tracking-widest mt-2">
                  Review Documentation
                </button>
              </div>
            </div>
          }
        </div>
        <div class="mt-6 overflow-hidden rounded-2xl border border-base-200 bg-base-100/40">
          <app-admin-paginator
            [totalItems]="pendingRiders().length"
            [currentPage]="currentPage()"
            [pageSize]="pageSize()"
            (pageChange)="currentPage.set($event)"
            (pageSizeChange)="changePageSize($event)"
          />
        </div>
      } @else {
        <!-- Empty State -->
        <div class="flex flex-col items-center justify-center py-32 space-y-6 opacity-20">
          <i class="fa-solid fa-file-shield text-9xl"></i>
          <div class="text-center">
            <h2 class="text-2xl font-black">All Caught Up!</h2>
            <p class="font-medium">No pending verification requests at the moment.</p>
          </div>
        </div>
      }

      <!-- Detail Modal -->
      @if (showDetail() && selectedRider()) {
        <app-rider-verification-detail
          [rider]="selectedRider()!"
          (closed)="showDetail.set(false)"
          (updated)="loadRiders()"
        ></app-rider-verification-detail>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }
  `]
})
export class RiderVerificationListComponent implements OnInit {
  private riderService = inject(RiderService);
  private snackbar = inject(SnackbarService);

  pendingRiders = signal<Rider[]>([]);
  isLoading = signal<boolean>(false);
  selectedRider = signal<Rider | null>(null);
  showDetail = signal<boolean>(false);
  currentPage = signal(1);
  pageSize = signal(10);
  paginatedRiders = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.pendingRiders().slice(start, start + this.pageSize());
  });

  ngOnInit(): void {
    this.loadRiders();
  }

  loadRiders() {
    this.isLoading.set(true);
    this.riderService.getAllRiders().subscribe({
      next: (data) => {
        this.pendingRiders.set(data.filter(r => r.verification_status === 'pending'));
        this.currentPage.set(1);
        this.isLoading.set(false);
      },
      error: () => {
        this.snackbar.error('Failed to load requests');
        this.isLoading.set(false);
      }
    });
  }

  viewDetail(rider: Rider) {
    this.selectedRider.set(rider);
    this.showDetail.set(true);
  }

  changePageSize(size: number): void {
    this.pageSize.set(size);
    this.currentPage.set(1);
  }
}
